import React, { lazy, Suspense } from 'react';
import { IonTabs, IonRouterOutlet, IonTabBar, IonTabButton, IonIcon, IonLabel } from '@ionic/react';
import { Route, Routes, Navigate } from 'react-router-dom';
import { ProtectedRoute } from './ProtectedRoute';
type ModuleWithPossibleDefault<T> = Partial<{ default: T } & Record<string, T>>;
const TeamMessagingMobile = lazy(() => (async () => {
  try {
    const mod = await import('../pages/teamMessaging/TeamMessagingMobile');
    const m = mod as ModuleWithPossibleDefault<React.ComponentType<unknown>>;
    console.debug('TeamTabs: TeamMessagingMobile module loaded', Object.keys(mod));
    return { default: (m.default || m.TeamMessagingMobile || (() => null)) as React.ComponentType<unknown> };
  } catch (err) {
    console.error('TeamTabs: Failed to load TeamMessagingMobile lazy module', err);
    const Fallback: React.FC = () => {
  const { t } = useTranslation();
  return (
      <div>
        <strong>{t('routing.TeamTabs.failedToLoadTeamMessagingMobile')}</strong>
        <div>{String(err)}</div>
      </div>
    );
};
    return { default: (Fallback as unknown) as React.ComponentType<unknown> };
  }
})());
const TeamMessagingDesktop = lazy(() => import('../desktop/pages/messaging/TeamMessagingDesktop').then((mod) => {
  const m = mod as ModuleWithPossibleDefault<React.ComponentType<unknown>>;
  console.debug('TeamTabs: TeamMessagingDesktop module loaded', Object.keys(mod));
  return { default: (m.default || m.TeamMessagingDesktop || (() => null)) as React.ComponentType<unknown> };
}));
const MembersPage = lazy(() => (async () => {
  try {
    const mod = await import('../pages/teamMessaging/MembersPage');
    const m = mod as ModuleWithPossibleDefault<React.ComponentType<unknown>>;
    console.debug('TeamTabs: MembersPage module loaded', Object.keys(mod));
    return { default: (m.default || m.MembersPage || (() => null)) as React.ComponentType<unknown> };
  } catch (err) {
    console.error('TeamTabs: Failed to load MembersPage lazy module', err);
    const Fallback: React.FC = () => {
  const { t } = useTranslation();
  return (
      <div>
        <strong>{t('routing.TeamTabs.failedToLoadTeamMembersPage')}</strong>
        <div>{String(err)}</div>
      </div>
    );
};
    return { default: (Fallback as unknown) as React.ComponentType<unknown> };
  }
})());
const TeamCalendar = lazy(() => (async () => {
  try {
    const mod = await import('../pages/teamMessaging/TeamCalendar');
    const m = mod as ModuleWithPossibleDefault<React.ComponentType<unknown>>;
    console.debug('TeamTabs: TeamCalendar module loaded', Object.keys(mod));
    return { default: (m.default || m.TeamCalendar || (() => null)) as React.ComponentType<unknown> };
  } catch (err) {
    console.error('TeamTabs: Failed to load TeamCalendar lazy module', err);
    const Fallback: React.FC = () => {
  const { t } = useTranslation();
  return (
      <div>
        <strong>{t('routing.TeamTabs.failedToLoadTeamCalendarPage')}</strong>
        <div>{String(err)}</div>
      </div>
    );
};
    return { default: (Fallback as unknown) as React.ComponentType<unknown> };
  }
})());
import { chatbubblesOutline, peopleOutline, calendarOutline, folderOutline, callOutline } from 'ionicons/icons';
import { OWNER_ROLES, MANAGER_ROLES, STAFF_ROLES } from '@rsmaids/shared/constants/roles';

import styles from '../pages/TeamMessagingPage.module.scss';
import { useTranslation } from 'react-i18next';

const Placeholder: React.FC<{title: string}> = ({title}) => {
  const { t } = useTranslation();
  return (
  <div className={styles.placeholder}>
    <h2>{title}</h2>
    <p>{t('routing.TeamTabs.comingSoon')}</p>
  </div>
);
};

const TeamTabs: React.FC = () => {
  const { t } = useTranslation();
  return (
    <IonTabs>
      <IonRouterOutlet>
      <Routes>
        <Route path="/team" element={<Navigate to="/team/members" replace />} />

        <Route path="/team/messages" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Suspense fallback={null}><TeamMessagingMobile /></Suspense>
          </ProtectedRoute>
        } />

        <Route path="/team/messages-desktop" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Suspense fallback={null}><TeamMessagingDesktop /></Suspense>
          </ProtectedRoute>
        } />

        <Route path="/team/members" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Suspense fallback={null}><MembersPage /></Suspense>
          </ProtectedRoute>
        } />

        <Route path="/team/calendar" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Suspense fallback={null}><TeamCalendar /></Suspense>
          </ProtectedRoute>
        } />

        <Route path="/team/files" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Placeholder title={t('routing.TeamTabs.files')} />
          </ProtectedRoute>
        } />

        <Route path="/team/calls" element={
          <ProtectedRoute allowedRoles={[...OWNER_ROLES, ...MANAGER_ROLES, ...STAFF_ROLES]}>
            <Placeholder title={t('routing.TeamTabs.calls')} />
          </ProtectedRoute>
        } />
      </Routes>
      </IonRouterOutlet>

      <IonTabBar slot="bottom">
        <IonTabButton tab="members" href="/team/members">
          <IonIcon icon={peopleOutline} className="icon-default" />
          <IonIcon icon={peopleOutline} className="icon-active" />
          <IonLabel>{t('routing.TeamTabs.members')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="chats" href="/team/messages">
          <IonIcon icon={chatbubblesOutline} className="icon-default" />
          <IonIcon icon={chatbubblesOutline} className="icon-active" />
          <IonLabel>{t('routing.TeamTabs.chats')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="calendar" href="/team/calendar">
          <IonIcon icon={calendarOutline} className="icon-default" />
          <IonIcon icon={calendarOutline} className="icon-active" />
          <IonLabel>{t('routing.TeamTabs.calendar')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="files" href="/team/files">
          <IonIcon icon={folderOutline} className="icon-default" />
          <IonIcon icon={folderOutline} className="icon-active" />
          <IonLabel>{t('routing.TeamTabs.files')}</IonLabel>
        </IonTabButton>
        <IonTabButton tab="calls" href="/team/calls">
          <IonIcon icon={callOutline} className="icon-default" />
          <IonIcon icon={callOutline} className="icon-active" />
          <IonLabel>{t('routing.TeamTabs.calls')}</IonLabel>
        </IonTabButton>
      </IonTabBar>
    </IonTabs>
  );
};

export default TeamTabs;
